import { raise } from '@sparkdotfi/common-universal'

import { getChainConfigEntry } from '@/config/chain'
import { trackEvent } from '@/domain/analytics/mixpanel'
import { WalletType } from '@/domain/hooks/useWalletType'
import { sanityCheckTx } from '@/features/actions/logic/transactions/sanityChecks'
import { ActionConfig, GetWriteConfigResult } from '@/features/actions/logic/types'
import { JSONStringifyRich } from '@/utils/object'

interface PreWriteAction {
  contract: GetWriteConfigResult
  beforeWriteCheck?: ActionConfig['beforeWriteCheck']
}

interface RunPreWriteChecksParams {
  actions: PreWriteAction[]
  chainId: number
}

export function runSingleActionPreWriteLifecycle({
  action,
  chainId,
  walletType,
}: {
  action: PreWriteAction
  chainId: number
  walletType: WalletType | undefined
}): void {
  runPreWriteChecks({ actions: [action], chainId })

  const originChainId = getChainConfigEntry(chainId).originChainId

  if (originChainId !== chainId) {
    return
  }

  const contract = action.contract
  trackEvent('tx-sent', {
    walletType,
    chainId,
    receiver: contract.address,
    method: contract.functionName,
    args: JSONStringifyRich(contract.args),
  })
}

export function runBatchPreWriteLifecycle({ actions, chainId }: { actions: PreWriteAction[]; chainId: number }): void {
  runPreWriteChecks({ actions, chainId })

  const originChainId = getChainConfigEntry(chainId).originChainId
  if (originChainId !== chainId) {
    return
  }

  const contracts = actions.map((action) => action.contract)
  trackEvent('tx-sent', {
    walletType: 'smart-wallet',
    args: JSONStringifyRich(contracts),
    chainId,
  })
}

function runPreWriteChecks({ actions, chainId }: RunPreWriteChecksParams): void {
  if (actions.length === 0) {
    raise('Cannot run pre-write checks for empty actions list')
  }

  for (const action of actions) {
    sanityCheckTx({ address: action.contract.address, value: action.contract.value }, chainId)
    action.beforeWriteCheck?.()
  }
}
